// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState } from 'react';
import { X } from 'lucide-react';

const BANKS = [
  'HDFC', 'ICICI', 'SBI', 'Axis', 'Kotak', 'IDBI', 'BoB', 'Federal',
  'Yes Bank', 'AMEX', 'Other'
];

const CARD_TYPES = ['CREDIT_CARD', 'DEBIT_CARD'];
const NETWORKS = ['Visa', 'Mastercard', 'Rupay', 'Amex'];

export default function PaymentCardForm({ initial, onSubmit, onClose }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    bank: initial?.bank || '',
    card_type: initial?.card_type || 'CREDIT_CARD',
    network: initial?.network || 'Visa',
    last_four_digits: initial?.last_four_digits || '',
    credit_limit: initial?.credit_limit || '',
    billing_day: initial?.billing_day || 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.bank || !form.last_four_digits) {
      setError('Please fill in required fields');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const payload = {
        ...form,
        billing_day: parseInt(form.billing_day),
        credit_limit: form.credit_limit ? parseFloat(form.credit_limit) : null,
      };
      await onSubmit(payload);
    } catch (err) {
      setError(err.message || 'Failed to save card');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">
          {initial ? 'Edit' : 'Add'} Payment Card
        </h2>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-400"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </p>
      )}

      {/* Name */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Card Name *
        </label>
        <input
          type="text"
          placeholder="e.g., My HDFC Credit Card"
          value={form.name}
          onChange={set('name')}
          required
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Bank + Card Type */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Bank *
          </label>
          <select
            value={form.bank}
            onChange={set('bank')}
            required
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Select…</option>
            {BANKS.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Type
          </label>
          <select
            value={form.card_type}
            onChange={set('card_type')}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {CARD_TYPES.map((t) => (
              <option key={t} value={t}>
                {t === 'CREDIT_CARD' ? 'Credit' : 'Debit'}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Network + Last 4 */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Network
          </label>
          <select
            value={form.network}
            onChange={set('network')}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {NETWORKS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Last 4 Digits *
          </label>
          <input
            type="text"
            placeholder="1234"
            maxLength="4"
            value={form.last_four_digits}
            onChange={set('last_four_digits')}
            required
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Limit + Billing Day */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Credit Limit (₹)
          </label>
          <input
            type="number"
            placeholder="50000"
            value={form.credit_limit}
            onChange={set('credit_limit')}
            min="0"
            step="1000"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Billing Day
          </label>
          <input
            type="number"
            min="1"
            max="31"
            value={form.billing_day}
            onChange={set('billing_day')}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="w-full py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? 'Saving…' : 'Save Card'}
      </button>
    </form>
  );
}
